import { supabase } from "@/lib/supabase";
import {
  adjustGuestProfileAfterMatch,
  getGuestProfile,
  type GuestBalanceHint,
  type GuestProfile,
  type GuestProfileCache,
  normalizeGuestName,
  upsertGuestProfile,
} from "@/lib/v2/guestBalance";

export async function fetchGuestProfiles(groupId: string): Promise<GuestProfileCache> {
  const { data, error } = await supabase
    .from("group_settings")
    .select("guest_profiles")
    .eq("group_id", groupId)
    .maybeSingle();

  if (error) throw error;
  const raw = data?.guest_profiles;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  return raw as GuestProfileCache;
}

export async function saveGuestProfiles(
  groupId: string,
  cache: GuestProfileCache
): Promise<void> {
  const { error } = await supabase
    .from("group_settings")
    .update({ guest_profiles: cache })
    .eq("group_id", groupId);

  if (error) throw error;
}

export async function rememberGuestAppearance(
  groupId: string,
  guestName: string,
  options: {
    balanceScore: number;
    hint?: GuestBalanceHint | null;
    won?: boolean;
  }
): Promise<void> {
  const key = normalizeGuestName(guestName);
  if (!key) return;

  const cache = await fetchGuestProfiles(groupId);
  const existing = getGuestProfile(cache, guestName);
  const base: GuestProfile = existing ?? {
    appearances: 0,
    balanceScore: options.balanceScore,
    hint: options.hint ?? undefined,
  };

  const updated =
    options.won != null
      ? adjustGuestProfileAfterMatch(base, options.won)
      : {
          ...base,
          appearances: base.appearances + 1,
          balanceScore: options.balanceScore,
          hint: options.hint ?? base.hint,
          lastSeen: new Date().toISOString().slice(0, 10),
        };

  await saveGuestProfiles(groupId, upsertGuestProfile(cache, guestName, updated));
}

export async function recordGuestLineupAppearances(
  groupId: string,
  guests: { guestName: string; balanceScore: number; won?: boolean }[]
): Promise<void> {
  if (guests.length === 0) return;

  let cache = await fetchGuestProfiles(groupId);
  for (const guest of guests) {
    const existing = getGuestProfile(cache, guest.guestName);
    const base: GuestProfile = existing ?? {
      appearances: 0,
      balanceScore: guest.balanceScore,
    };
    const updated =
      guest.won != null
        ? adjustGuestProfileAfterMatch(base, guest.won)
        : {
            ...base,
            appearances: base.appearances + 1,
            balanceScore: guest.balanceScore,
            lastSeen: new Date().toISOString().slice(0, 10),
          };
    cache = upsertGuestProfile(cache, guest.guestName, updated);
  }
  await saveGuestProfiles(groupId, cache);
}
